// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Query, graphql } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import * as Sentry from '@sentry/browser'

import {
  ExpandableOverviewItem,
  LabelledDetail,
  Button,
  TextArea,
  Table,
  SelectIdentityBankAccount
} from '../'

import { ReadOnlyContext } from '../../context'

import { decodeBase64 } from '../../utils'

import { WORKFLOW_LOAN_DISBURSAL } from '../../constants'

import { CreditDetails } from './CreditDetails'


class CreditApplicationIssue extends React.Component {
  static propTypes = {
    issuedBy: PropTypes.object,
    issuerNotes: PropTypes.string,
    approverNotes: PropTypes.string,
    open: PropTypes.bool.isRequired,
    updateCreditApplication: PropTypes.func.isRequired,
    updateCredit: PropTypes.func.isRequired,
    creditApplication: PropTypes.shape({
      id: PropTypes.string.isRequired,
      eligibleLoanAmount: PropTypes.string,
      status: PropTypes.string
    }).isRequired,
    credit: PropTypes.shape({
      id: PropTypes.string.isRequired,
      creditType: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
      instalmentAgreed: PropTypes.string.isRequired
    }),
    onClick: PropTypes.func.isRequired,
    collapseSectionOnComplete: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  state = {
    issuerNotes: this.props.issuerNotes || '',
    saveInProgress: false,
    selectedBankAccountId: this.props.creditApplication.bankaccount
      ? this.props.creditApplication.bankaccount.id
      : null
  }

  handleIssuerNotesChange = (e) => this.setState({ issuerNotes: e.target.value })

  handleCancelClick = () => this.setState({ issuerNotes: this.props.issuerNotes || '' })

  handleIssueClick = () => {
    this.setState({ saveInProgress: true }, () => {
      // update the credit application
      let params = {
        issuedBy: localStorage.getItem("userUUID"),
        issuedAt: moment.utc().format("YYYY-MM-DD HH:mm:ss.SSS"),
        status: "credits_pending_disbursements",
      };

      if (!this.issuerNotesUnchanged()) {
        params.issuerNotes = this.state.issuerNotes
      }

      if (this.props.creditApplication.creditType === 'CASH' &&
        this.state.selectedBankAccountId !== null &&
        (
          this.props.creditApplication.bankaccount === null ||
          this.state.selectedBankAccountId !== this.props.creditApplication.bankaccount.id
        )) {

        params.bankaccount = this.state.selectedBankAccountId
      }

      this.props.updateCreditApplication(params)
      .then(this.disburse())
      .then(() => {
        setTimeout(
          () => {
            this.props.collapseSectionOnComplete()  // loan disbursal workflow run successfully created at this stage
            this.setState({ saveInProgress: false })
          },
          5000  // 5 seconds
        )
      })
      .catch(error => {
        Sentry.captureMessage(`Failed to update credit application - ${error}`)
        this.setState({ saveInProgress: false })
      })
    })
  }

  disburse = () => {
    // initiate disbursal workflow
    this.initiateLoanDisbursalWorkflowRun()
    .catch(error => {
      Sentry.captureMessage(`Failed to initiate loan disbursal workflow - ${error}`)
      this.setState({ saveInProgress: false })
    })
  }

  initiateLoanDisbursalWorkflowRun = () => {
    let input = {
      workflow: WORKFLOW_LOAN_DISBURSAL,
      version: 1,
      status: 'pending',
      parameters: JSON.stringify({ credit: decodeBase64(this.props.credit.id) })
    }

    return this.props.initiateLoanDisbursalWorkflowRun({ variables: { input } })
  }

  handleRemoveFinalisationClick = (userName) => {
    // append to approverNotes
    const finaliseRemovedLine = `\n\n=== Finalisation removed by ${userName} at ${moment().format('DD-MM-YYYY HH:mm')} ===\n\n`
    const outcomeNotes = `${this.props.outcomeNotes ? this.props.outcomeNotes : ''}${finaliseRemovedLine}`

    this.props.updateCreditApplication({
      finalisedBy: 'null',
      approvedBy: 'null',
      outcome: null,
      status: 'incomplete',
      outcomeNotes
    })
    .then(this.props.collapseSectionOnComplete)
  }

  issuerNotesUnchanged = () => {
    if (this.props.issuerNotes) {
      return this.state.issuerNotes === this.props.issuerNotes
    } else {
      return this.state.issuerNotes === ''
    }
  }

  getSummary = (issued, issuedBy, approved) => {
    if (issued) {
      return `Loan issued by ${issuedBy.fullName}`
    } else if (!approved) {
      return 'Requires approval'
    }
  }

  getIconSymbol = (issued) => {
    if (issued) {
      return 'check'
    } else {
      return null
    }
  }

  getIconColour = (issued) => {
    if (issued) {
      return 'success'
    } else {
      return null
    }
  }

  saveBankAccountSelection = (e) => {
    this.setState({ selectedBankAccountId: e.target.value })
  }

  render() {
    const { issuedBy, status, open, credit, creditApplication, onClick, className } = this.props
    const issueComplete = issuedBy !== null
    const approved = status === 'APPROVED'
    const disbursalWorkflowExists = this.props.credit && this.props.credit.workflowrunProcessing !== null
    const isCashLoanApplication = creditApplication.creditType === 'CASH'

    return (
      <ExpandableOverviewItem
        title='Issue'
        summary={this.getSummary(issueComplete, issuedBy, approved)}
        iconSymbol={this.getIconSymbol(issueComplete, disbursalWorkflowExists)}
        iconColour={this.getIconColour(issueComplete, disbursalWorkflowExists)}
        open={open}
        locked={disbursalWorkflowExists}
        onClick={onClick}
        className={className}>

        <div className='slds-m-left_small slds-m-top_small slds-m-bottom_medium'>
          {credit &&
            <ReadOnlyContext.Consumer>
              {readOnly => (
                <Fragment>
                  {isCashLoanApplication &&
                    <SelectIdentityBankAccount
                      label='Choose bank account for loan payment'
                      identityId={creditApplication.client.identity.id}
                      value={this.state.selectedBankAccountId}
                      onChange={this.saveBankAccountSelection}
                      disabled={issueComplete || !approved || readOnly}
                      className='slds-m-bottom_large'
                    />
                  }
                  <span className='slds-text-color_weak'>
                    Loan details
                  </span>
                  <Table
                    columns={[ 'type', 'eligible for', 'amount', 'instalment']}
                    rows={[
                      <CreditDetails
                        creditType={credit.creditType}
                        eligibleFor={creditApplication.overrideStatus === 'COMPLETED'
                          ? creditApplication.overrideEligibleLoanAmount
                          : creditApplication.eligibleLoanAmount
                        }
                        amount={credit.amount}
                        instalment={credit.instalmentAgreed}
                        workflowRunProcessing={credit.workflowrunProcessing}
                        longtermContributionRequired={creditApplication.securityLtContributionActual}
                        key={`creditDetails_${credit.id}`}
                      />
                    ]}
                    objectRows={true}
                    noCellPadding={true}
                    className='slds-m-bottom_large'
                  />
                  <div className='slds-size_3-of-4'>
                    <LabelledDetail
                      label='Notes'
                      detail={
                        <TextArea
                          placeholder='Add issuer notes'
                          value={this.state.issuerNotes}
                          onChange={this.handleIssuerNotesChange}
                          rows={5}
                          disabled={issueComplete || !approved || readOnly}
                        />
                      }
                      detailFontSize='inherit'
                    />
                  </div>
                  <div className='slds-grid slds-align_absolute-center slds-m-top_large'>
                    <div className='slds-col_bump-left slds-m-right_xx-large'>
                      <Query query={gql`query { me { firstName lastName } }`}>
                        {({ data }) => (
                          <Button
                            onClick={() => (
                              data
                                ? this.handleRemoveFinalisationClick(`${data.me.firstName} ${data.me.lastName}`)
                                : false
                            )}
                            disabled={readOnly || !data || issueComplete || this.state.saveInProgress}
                            title='This will reset the finalised and approved steps'
                            className='slds-m-right_large'>

                            Remove finalisation
                          </Button>
                        )}
                      </Query>
                      <Button
                        minWidth={90}
                        onClick={this.handleCancelClick}
                        disabled={this.issuerNotesUnchanged() || this.state.saveInProgress || !approved}>
                        Cancel
                      </Button>
                      <Button
                        type='brand'
                        minWidth={90}
                        onClick={this.handleIssueClick}
                        disabled={this.issuerNotesUnchanged() ||
                          this.state.saveInProgress ||
                          !approved ||
                          readOnly ||
                          (isCashLoanApplication && this.state.selectedBankAccountId === null)
                        }
                        className='slds-m-left_small'>

                        {this.state.saveInProgress
                          ? 'Saving...'
                          : 'Issue'
                        }
                      </Button>
                    </div>
                  </div>
                </Fragment>
              )}
            </ReadOnlyContext.Consumer>
          }
        </div>
      </ExpandableOverviewItem>
    )
  }
}

const initiateLoanDisbursalWorkflowRun = gql`
  mutation ($input: WorkflowRunMutationInput!) {
    workflowrunMutate(input: $input) {
      workflowrun {
        id
      }
    }
  }
`

const withWorkflowRunMutate = graphql(
  initiateLoanDisbursalWorkflowRun, { name: "initiateLoanDisbursalWorkflowRun"}
)

export default withWorkflowRunMutate(CreditApplicationIssue)



// WEBPACK FOOTER //
// ./src/components/creditapplication-issue/index.js